import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Heart, Minus, Plus } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { Seo } from '../../../../components/seo.js';
import { formatPaise } from '../../../../lib/format.js';
import { StorefrontApiError } from '../../../../lib/api/storefront-client.js';
import { StorefrontEmptyState, StorefrontErrorState, StorefrontLoadingState } from '../../components/states.js';
import { useProduct, useProducts } from '../hooks/use-catalog.js';
import { useCartMutations } from '../../cart/hooks/use-cart.js';
import { useWishlist, useWishlistMutations } from '../../wishlist/hooks/use-wishlist.js';
import { useServiceabilityCheck } from '../../delivery/hooks/use-delivery.js';
import { ProductCard } from '../components/product-card.js';
import type { ProductImage, ProductVariant } from '../types/catalog.js';
import type { ServiceabilityReason } from '../../delivery/types/delivery.js';

/**
 * There is no id-based lookup for category/brand -- `apps/worker/src/routes/v1/catalog.ts`
 * only exposes `/categories/:slug` and `/brands/:slug`, and `ProductDetail`
 * only carries `categoryId`/`brandId`. Fetching every category/brand page
 * just to resolve one name client-side isn't a reasonable trade for this
 * page, so brand/category names are intentionally omitted here rather than
 * built via a workaround chain -- a real gap, not fabricated data.
 */

const REASON_COPY: Record<ServiceabilityReason, string> = {
  not_listed: "We don't deliver to this pincode yet.",
  pincode_disabled: 'Delivery to this pincode is temporarily paused.',
  zone_disabled: 'Delivery to this area is temporarily unavailable.',
  no_active_fee_rule: "Delivery isn't set up for this area yet.",
};

const PINCODE_PATTERN = /^[1-9]\d{5}$/;

function sortImages(images: ProductImage[]): ProductImage[] {
  return [...images].sort((a, b) => a.sortOrder - b.sortOrder);
}

function pickDefaultVariant(variants: ProductVariant[]): ProductVariant | undefined {
  return variants.find((variant) => variant.stock.status !== 'out_of_stock') ?? variants[0];
}

export function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const productQuery = useProduct(slug);
  const product = productQuery.data;

  const { add: addToCart } = useCartMutations();
  const wishlistQuery = useWishlist();
  const { add: addToWishlist, remove: removeFromWishlist } = useWishlistMutations();
  const serviceability = useServiceabilityCheck();

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>(undefined);
  const [quantity, setQuantity] = useState(1);
  const [pincode, setPincode] = useState('');
  const [pincodeTouched, setPincodeTouched] = useState(false);
  const [cartMessage, setCartMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [wishlistMessage, setWishlistMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const thumbRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Default the selected image/variant once the product loads.
  useEffect(() => {
    if (!product) return;
    const sorted = sortImages(product.images);
    const primaryIndex = sorted.findIndex((image) => image.isPrimary);
    setSelectedImageIndex(primaryIndex >= 0 ? primaryIndex : 0);
    setSelectedVariantId((current) => current ?? pickDefaultVariant(product.variants)?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Related products -- reasonable "you might also like" using the real
  // category listing endpoint since there's no dedicated related-products
  // route. The hook itself is still called unconditionally (Rules of
  // Hooks) with a stable `params` shape, but `enabled` keeps it from
  // firing against the backend until `product.categoryId` actually
  // exists -- before that, `categoryId` has no valid value at all (an
  // empty string is not a category id), so the request must not go out.
  const relatedCategoryId = product?.categoryId;
  const relatedQuery = useProducts(
    { categoryId: relatedCategoryId, limit: 9, offset: 0 },
    { enabled: Boolean(relatedCategoryId) },
  );
  const relatedProducts = useMemo(
    () => (relatedQuery.data ?? []).filter((item) => item.id !== product?.id).slice(0, 8),
    [relatedQuery.data, product?.id],
  );

  if (!slug) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <StorefrontEmptyState title="Product not found" description="No product slug was provided." />
      </div>
    );
  }

  if (productQuery.isLoading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-16">
        <StorefrontLoadingState label="Loading product…" />
      </div>
    );
  }

  if (productQuery.isError) {
    const isNotFound = productQuery.error instanceof StorefrontApiError && productQuery.error.status === 404;
    if (isNotFound) {
      return (
        <>
          <Seo title="Product not found" noindex />
          <div className="mx-auto max-w-3xl px-4 py-16">
            <StorefrontEmptyState
              title="Product not found"
              description="This product may have been removed, or the link you followed is incorrect."
            />
          </div>
        </>
      );
    }
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <StorefrontErrorState
          message={productQuery.error instanceof StorefrontApiError ? productQuery.error.message : undefined}
          onRetry={() => void productQuery.refetch()}
        />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-16">
        <StorefrontLoadingState label="Loading product…" />
      </div>
    );
  }

  // Mandatory canonical-slug redirect: the backend resolves historical
  // slugs too but never issues an HTTP redirect (documented as deferred to
  // this page). `replace` so the old URL doesn't linger in history.
  if (!product.canonical.isCanonical) {
    return <Navigate to={`${ROUTE_NAMES.product}/${product.canonical.canonicalSlug}`} replace />;
  }

  const sortedImages = sortImages(product.images);
  const activeImage = sortedImages[selectedImageIndex];
  const selectedVariant =
    product.variants.find((variant) => variant.id === selectedVariantId) ?? product.variants[0];
  const allOutOfStock =
    product.variants.length === 0 || product.variants.every((variant) => variant.stock.status === 'out_of_stock');

  const hasDiscount = Boolean(
    selectedVariant && selectedVariant.mrp.amountPaise > selectedVariant.sellingPrice.amountPaise,
  );
  const discountPercent =
    selectedVariant && hasDiscount
      ? Math.round(
          ((selectedVariant.mrp.amountPaise - selectedVariant.sellingPrice.amountPaise) /
            selectedVariant.mrp.amountPaise) *
            100,
        )
      : 0;

  const maxQuantity = !selectedVariant
    ? 1
    : selectedVariant.stock.status === 'out_of_stock'
      ? 0
      : selectedVariant.stock.status === 'low_stock'
        ? Math.max(1, selectedVariant.stock.available)
        : 99;

  const wishlistItem = wishlistQuery.data?.find((item) => item.productId === product.id);
  const isWishlisted = Boolean(wishlistItem);

  const seoTitle = product.metaTitle || product.name;
  const seoDescription = product.metaDescription || (product.description ? product.description.slice(0, 160) : undefined);
  const structuredData: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    ...(product.description ? { description: product.description } : {}),
    ...(activeImage?.url ? { image: activeImage.url } : {}),
    ...(selectedVariant
      ? {
          offers: {
            '@type': 'Offer',
            price: (selectedVariant.sellingPrice.amountPaise / 100).toFixed(2),
            priceCurrency: selectedVariant.sellingPrice.currency,
            availability:
              selectedVariant.stock.status === 'out_of_stock'
                ? 'https://schema.org/OutOfStock'
                : 'https://schema.org/InStock',
          },
        }
      : {}),
    // No ratings/reviews anywhere in this codebase -- never fabricate
    // `aggregateRating`/`review` fields here.
  };

  function focusAndSelectImage(index: number) {
    if (sortedImages.length === 0) return;
    const clamped = Math.max(0, Math.min(index, sortedImages.length - 1));
    setSelectedImageIndex(clamped);
    thumbRefs.current[clamped]?.focus();
  }

  function handleThumbKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusAndSelectImage(index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusAndSelectImage(index - 1);
    }
  }

  function handleSelectVariant(variantId: string) {
    setSelectedVariantId(variantId);
    setQuantity(1);
    setCartMessage(null);
  }

  function handleQuantityChange(next: number) {
    if (maxQuantity <= 0) return;
    setQuantity(Math.min(Math.max(1, Math.round(next) || 1), maxQuantity));
  }

  function handleAddToCart() {
    if (!selectedVariant || selectedVariant.stock.status === 'out_of_stock') return;
    setCartMessage(null);
    addToCart.mutate(
      { variantId: selectedVariant.id, quantity },
      {
        onSuccess: () =>
          setCartMessage({ type: 'success', text: `Added ${quantity} × ${selectedVariant.variantName} to your cart.` }),
        onError: (error) =>
          setCartMessage({
            type: 'error',
            text: error instanceof StorefrontApiError ? error.message : 'Could not add to cart. Please try again.',
          }),
      },
    );
  }

  function handleToggleWishlist() {
    setWishlistMessage(null);
    if (isWishlisted && wishlistItem) {
      removeFromWishlist.mutate(wishlistItem.id, {
        onError: (error) =>
          setWishlistMessage({
            type: 'error',
            text: error instanceof StorefrontApiError ? error.message : 'Could not update your wishlist.',
          }),
      });
    } else {
      addToWishlist.mutate(product!.id, {
        onSuccess: () => setWishlistMessage({ type: 'success', text: 'Added to your wishlist.' }),
        onError: (error) =>
          setWishlistMessage({
            type: 'error',
            text: error instanceof StorefrontApiError ? error.message : 'Could not update your wishlist.',
          }),
      });
    }
  }

  function handlePincodeSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPincodeTouched(true);
    if (!PINCODE_PATTERN.test(pincode)) return;
    serviceability.mutate(pincode);
  }

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonicalPath={`${ROUTE_NAMES.product}/${product.canonical.canonicalSlug}`}
        ogType="product"
        image={activeImage?.url ?? undefined}
        structuredData={structuredData}
      />
      <div className="mx-auto max-w-6xl px-4 pb-28 pt-6 md:pb-16">
        <div className="grid gap-8 md:grid-cols-2">
          {/* Gallery */}
          <div>
            <div className="flex aspect-square items-center justify-center overflow-hidden rounded-2xl border border-gray-100 bg-gray-50">
              {activeImage?.url ? (
                <img
                  src={activeImage.url}
                  alt={activeImage.altText ?? product.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-sm text-gray-400">No image available</span>
              )}
            </div>
            {sortedImages.length > 1 && (
              <div role="tablist" aria-label="Product images" className="mt-3 flex gap-2 overflow-x-auto">
                {sortedImages.map((image, index) => (
                  <button
                    key={image.id}
                    ref={(el) => {
                      thumbRefs.current[index] = el;
                    }}
                    type="button"
                    role="tab"
                    aria-selected={index === selectedImageIndex}
                    aria-label={`Show image ${index + 1} of ${sortedImages.length}`}
                    tabIndex={index === selectedImageIndex ? 0 : -1}
                    onClick={() => setSelectedImageIndex(index)}
                    onKeyDown={(event) => handleThumbKeyDown(event, index)}
                    className={`focus-visible:ring-brand flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-gray-50 focus-visible:outline-none focus-visible:ring-2 ${
                      index === selectedImageIndex ? 'border-gray-900' : 'border-gray-200'
                    }`}
                  >
                    {image.url ? (
                      <img src={image.url} alt={image.altText ?? ''} loading="lazy" className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-[10px] text-gray-400">No image</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{product.name}</h1>
            {product.description && <p className="mt-2 text-sm text-gray-600">{product.description}</p>}

            {allOutOfStock ? (
              <div className="mt-6">
                <StorefrontEmptyState
                  title="Currently unavailable"
                  description="This product is out of stock right now. Please check back later."
                />
              </div>
            ) : selectedVariant ? (
              <>
                {/* Price */}
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-gray-900">
                    {formatPaise(selectedVariant.sellingPrice.amountPaise)}
                  </span>
                  {hasDiscount && (
                    <>
                      <span className="text-sm text-gray-400 line-through">
                        {formatPaise(selectedVariant.mrp.amountPaise)}
                      </span>
                      <span className="text-sm font-semibold text-green-600">{discountPercent}% off</span>
                    </>
                  )}
                </div>

                {/* Stock */}
                <p className="mt-1 text-sm" aria-live="polite">
                  {selectedVariant.stock.status === 'in_stock' && <span className="text-green-600">In stock</span>}
                  {selectedVariant.stock.status === 'low_stock' && (
                    <span className="text-amber-600">Only {selectedVariant.stock.available} left</span>
                  )}
                  {selectedVariant.stock.status === 'out_of_stock' && <span className="text-red-600">Out of stock</span>}
                </p>

                {/* Variant selector */}
                {product.variants.length > 1 && (
                  <fieldset className="mt-4">
                    <legend className="text-sm font-medium text-gray-900">Pack size</legend>
                    <div role="radiogroup" aria-label="Pack size" className="mt-2 flex flex-wrap gap-2">
                      {product.variants.map((variant) => (
                        <button
                          key={variant.id}
                          type="button"
                          role="radio"
                          aria-checked={variant.id === selectedVariant.id}
                          onClick={() => handleSelectVariant(variant.id)}
                          className={`focus-visible:ring-brand rounded-full border px-4 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 ${
                            variant.stock.status === 'out_of_stock' ? 'opacity-40' : ''
                          } ${
                            variant.id === selectedVariant.id
                              ? 'border-gray-900 bg-gray-900 text-white'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                          }`}
                        >
                          {variant.variantName}
                        </button>
                      ))}
                    </div>
                  </fieldset>
                )}

                {/* Quantity */}
                <div className="mt-4">
                  <label htmlFor="product-quantity" className="text-sm font-medium text-gray-900">
                    Quantity
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="Decrease quantity"
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={maxQuantity <= 0 || quantity <= 1}
                      className="focus-visible:ring-brand flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 text-gray-700 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2"
                    >
                      <Minus className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <input
                      id="product-quantity"
                      type="number"
                      inputMode="numeric"
                      aria-label="Quantity"
                      min={1}
                      max={maxQuantity || 1}
                      value={quantity}
                      disabled={maxQuantity <= 0}
                      onChange={(event) => handleQuantityChange(Number(event.target.value))}
                      className="w-16 rounded-lg border border-gray-300 px-2 py-1.5 text-center text-sm"
                    />
                    <button
                      type="button"
                      aria-label="Increase quantity"
                      onClick={() => handleQuantityChange(quantity + 1)}
                      disabled={maxQuantity <= 0 || quantity >= maxQuantity}
                      className="focus-visible:ring-brand flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 text-gray-700 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2"
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                {/* Add to cart + wishlist */}
                <div className="mt-6 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    disabled={!selectedVariant || selectedVariant.stock.status === 'out_of_stock' || addToCart.isPending}
                    className="focus-visible:ring-brand flex-1 rounded-full bg-gray-900 px-6 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2"
                  >
                    {selectedVariant.stock.status === 'out_of_stock'
                      ? 'Out of stock'
                      : addToCart.isPending
                        ? 'Adding…'
                        : 'Add to cart'}
                  </button>
                  <button
                    type="button"
                    onClick={handleToggleWishlist}
                    aria-pressed={isWishlisted}
                    aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                    className="focus-visible:ring-brand flex h-11 w-11 items-center justify-center rounded-full border border-gray-300 focus-visible:outline-none focus-visible:ring-2"
                  >
                    <Heart
                      className={`h-5 w-5 ${isWishlisted ? 'fill-red-500 text-red-500' : 'text-gray-500'}`}
                      aria-hidden="true"
                    />
                  </button>
                </div>

                {cartMessage && (
                  <p role={cartMessage.type === 'error' ? 'alert' : 'status'} className={`mt-2 text-sm ${cartMessage.type === 'error' ? 'text-red-600' : 'text-green-600'}`}>
                    {cartMessage.text}
                  </p>
                )}
                {wishlistMessage && (
                  <p role={wishlistMessage.type === 'error' ? 'alert' : 'status'} className={`mt-2 text-sm ${wishlistMessage.type === 'error' ? 'text-red-600' : 'text-green-600'}`}>
                    {wishlistMessage.text}
                  </p>
                )}
              </>
            ) : null}

            {/* Delivery serviceability */}
            <div className="mt-8 rounded-2xl border border-gray-100 p-4">
              <h2 className="text-sm font-semibold text-gray-900">Check delivery</h2>
              <form onSubmit={handlePincodeSubmit} className="mt-2 flex gap-2">
                <label htmlFor="pincode-input" className="sr-only">
                  Pincode
                </label>
                <input
                  id="pincode-input"
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter pincode"
                  value={pincode}
                  onChange={(event) => setPincode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-32 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <button
                  type="submit"
                  disabled={serviceability.isPending}
                  className="focus-visible:ring-brand rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2"
                >
                  {serviceability.isPending ? 'Checking…' : 'Check'}
                </button>
              </form>
              {pincodeTouched && !PINCODE_PATTERN.test(pincode) && (
                <p role="alert" className="mt-2 text-sm text-red-600">
                  Enter a valid 6-digit pincode.
                </p>
              )}
              {serviceability.isError && (
                <p role="alert" className="mt-2 text-sm text-red-600">
                  Could not check serviceability. Please try again.
                </p>
              )}
              {serviceability.data && (
                <p role="status" className="mt-2 text-sm">
                  {serviceability.data.serviceable ? (
                    <span className="text-green-600">
                      Delivers here
                      {serviceability.data.etaMinHours !== null && serviceability.data.etaMaxHours !== null
                        ? ` in ${serviceability.data.etaMinHours}-${serviceability.data.etaMaxHours} hours`
                        : ''}
                      {serviceability.data.deliveryZoneName ? ` (${serviceability.data.deliveryZoneName})` : ''}
                      .
                    </span>
                  ) : (
                    <span className="text-red-600">
                      {serviceability.data.reason ? REASON_COPY[serviceability.data.reason] : 'Not deliverable to this pincode.'}
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Related products */}
        {relatedProducts.length > 0 && (
          <section className="mt-12">
            <h2 className="text-lg font-bold text-gray-900">You might also like</h2>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {relatedProducts.map((item) => (
                <ProductCard key={item.id} product={item} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Mobile sticky purchase bar */}
      {!allOutOfStock && selectedVariant && (
        <div className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3 shadow-lg md:hidden">
          <div>
            <p className="text-base font-bold text-gray-900">{formatPaise(selectedVariant.sellingPrice.amountPaise)}</p>
            <p className="text-xs text-gray-500">Qty {quantity}</p>
          </div>
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={selectedVariant.stock.status === 'out_of_stock' || addToCart.isPending}
            className="focus-visible:ring-brand flex-1 rounded-full bg-gray-900 px-6 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2"
          >
            {selectedVariant.stock.status === 'out_of_stock' ? 'Out of stock' : 'Add to cart'}
          </button>
        </div>
      )}
    </>
  );
}
